import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { FormValidator } from '../../global/form-validator';
import { Labels } from '../../global/labels';
import { rebuild } from '../../global/mutations/index';
import { createElement } from '../../global/render-api';
import {
    dispatchCustomEvent,
    getNativeInputDescriptor,
    setNativeInputValue,
} from '../../global/ui-helpers';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { CustomFormComponent } from '../../types/custom-form-component';
import type { TranslationLabels } from '../../types/labels.type';
import { UIDropdown } from '../dropdown/ui-dropdown';
import { UIField } from '../field/ui-field';
import { UIForm } from '../form/ui-form';
import { UIHint } from '../hint/ui-hint';
import { UIElement } from '../ui-element';
import type { AttributeValue } from '../ui-element.type';
import countries from './baltic-region-countries';
import styles from './ui-phonefield.css';
import type {
    UIPhoneFieldCountry,
    UIPhoneFieldParsedValue,
    UIPhoneFieldRulesKey,
    UIPhoneFieldRulesMap,
} from './ui-phonefield.type';
import { runInNextFrame } from '../../global/helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIPhoneField
 * @element ui-phonefield
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-phonefield</code> element.
 * Element is a simple way to add phone field, with required functionality.
 * @property {string} value {@attr value} Value of the field, which is a combination of selected country dial code and phone number input value. For example: <code>+37212345678</code>.
 * @property {("ee" | "lv" | "lt" | "default")} rules {@attr rules} Sorting rules applied to the incoming countries data based on <code>UIElement.LANGUAGE</code>. Does not apply if custom countries data is supplied.
 * @property {Array<UIPhoneFieldCountry> | null} countriesData Custom per-instance countries data. When set, it takes precedence over global countries data set by static method <code>UIPhoneField.setCountriesData</code> for all instances on a page, and skips built-in rules sorting.
 * @property {UIDropdown} dropdown Shortcut to dropdown with countries.
 * @property {HTMLInputElement} control Shortcut to visible input control with phone number.
 * @property {HTMLInputElement} valueControl Shortcut to a hidden input that holds full value with code and phone number.
 * @slot
 * @example
 * <ui-phonefield rules="ee">
 *   <input type="tel" name="some-phone" id="some-phone-1"/>
 * </ui-phonefield>
 */
@Component({ tag: 'ui-phonefield', styles })
export class UIPhoneField extends UIElement implements CustomFormComponent {
    @Attribute(String) rules?: string;
    @Attribute(String) controlId?: string;

    @Child('.ui-phonefield__dropdown') dropdown!: UIDropdown;
    @Child('.ui-phonefield__control') control!: HTMLInputElement;
    @Child('.ui-phonefield__value') valueControl!: HTMLInputElement;

    private _countriesData: NullOr<Array<UIPhoneFieldCountry>> = null;
    private initialValue: string = '';
    private readonly dropdownChangeHandler =
        this.handleDropdownChange.bind(this);
    private readonly valueChangeHandler = this.handleValueChange.bind(this);

    static countries: Array<UIPhoneFieldCountry>;
    static rules: UIPhoneFieldRulesKey;

    /**
     * @type {string}
     */
    static readonly ObjectChangeEvent: string = 'ui-phonefield-change';

    get value(): string {
        return this.valueControl.value;
    }

    set value(v: string) {
        setNativeInputValue(this.control, v.replace(this.dropdown.value, ''));
        setNativeInputValue(this.valueControl, v);
    }

    /**
     * Gets or sets custom countries data for this component instance. When set, it takes precedence over global countries data set by static method <code>UIPhoneField.setCountriesData</code> for all instances on a page, and skips built-in rules sorting.
     * @type {Array<UIPhoneFieldCountry> | null}
     */
    get countriesData(): NullOr<Array<UIPhoneFieldCountry>> {
        return this._countriesData ?? null;
    }

    set countriesData(data: NullOr<Array<UIPhoneFieldCountry>>) {
        this._countriesData = Array.isArray(data) ? data : null;
        this.handleCountriesDataChange();
    }

    /**
     * How many countries will be visible in dropdown without scrolling.
     * @type {number}
     */
    static readonly MAX_COUNTRIES_DISPLAY: number = 7;

    /**
     * Gets the validator if exists.
     * @type {null|SharedComponents.FormValidator}
     */
    get validator(): NullOr<FormValidator> {
        const form = this.closest<UIForm>('form');
        if (form?.validator) {
            return form.validator;
        } else {
            return null;
        }
    }

    /**
     * Define rules of how phone codes are pre-sorted in the dropdown in different countries. Code options are the same, but order is different. The actual countries to be sorted are provided through setCountriesData method.
     * @type {UIPhoneFieldRulesMap}
     */
    static readonly RULES: UIPhoneFieldRulesMap = {
        ee: {
            sortOrder: ['ee', 'lv', 'lt', 'se', 'fi', 'ru'],
            pattern: '[0-9]{5,11}',
        },
        lv: {
            sortOrder: ['lv', 'ee', 'lt', 'se', 'fi', 'ru'],
            pattern: '[0-9]{5,11}',
        },
        lt: {
            sortOrder: ['lt', 'lv', 'ee', 'se', 'fi', 'ru'],
            pattern: '[0-9]{5,11}',
        },
        default: {
            sortOrder: ['ee', 'lv', 'lt', 'se', 'fi', 'ru'],
            pattern: '[0-9]{5,11}',
        },
    };

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-phonefield', {
            INVALID: 'The phone number entered is invalid.',
            CHOOSE_COUNTRY: 'Select country',
            PHONENR: 'Phone number',
            NUMBER: 'number',
        });
    }

    /**
     * Sets global countries data for all phone field instances on the page.
     * This data is used by instances without custom per-instance countries data, filtered and sorted according to each instance's <code>rules</code> attribute.
     * Note it does not trigger reactive updates to existing instances — they will only use the new data on re-render or when explicitly refreshed.
     * Maintained for compatibility with country-filler plugin in iBank.
     * @param {Array<UIPhoneFieldCountry>} data
     * @deprecated Use instance-level <code>countriesData</code> setter or instance-level <code>setCountriesData</code> method.
     */
    static setCountriesData(data: Array<UIPhoneFieldCountry>): void {
        this.countries = data;
    }

    /**
     * Sets per-instance countries data. When used, global countries data and
     * rules-based presorting are ignored for this instance. It is assumed that the provided countries list is already sorted in the desired order.
     * @param {Array<UIPhoneFieldCountry> | null} data
     * @returns {UIPhoneField}
     */
    setCountriesData(data: NullOr<Array<UIPhoneFieldCountry>>): UIPhoneField {
        this.countriesData = data;
        return this;
    }

    getControlNativeValue(): string {
        return this.control
            ? (getNativeInputDescriptor().get!.call(this.control) as string)
            : '';
    }

    /**
     * Obtains the longest dial code from value that matches any of the countries in the dropdown.
     * @param {string} value
     * @returns {string}
     * @private
     */
    private resolveDialCodeFromValue(value?: string): string {
        let code = '';
        let dialCode = '';
        const cleanedValue = this.clean(value);

        for (let i = 0; i < 4; ++i) {
            code += cleanedValue.charAt(i);
            // Check if this code exists in the current list of countries visible in the dropdown
            if (this.getCountryByDialCode(code)) {
                dialCode = code;
            }
        }

        return dialCode;
    }

    /**
     * Returns current raw value that can be reparsed against the current countries list.
     * @returns {string | undefined}
     * @private
     */
    private pickValueSourceWithDialCode(): UndefinedOr<string> {
        const controlValue = this.getControlNativeValue();
        const valueSources = [
            this.initialValue,
            controlValue,
            this.valueControl?.value,
        ];

        return valueSources.find((value) =>
            this.resolveDialCodeFromValue(value)
        );
    }

    /**
     * Returns selected dial code inferred from current raw values.
     * @returns {string | undefined}
     * @private
     */
    private getDialCodeForOptionSelection(): UndefinedOr<string> {
        const dialCode = this.resolveDialCodeFromValue(
            this.pickValueSourceWithDialCode()
        );

        if (dialCode) {
            return '+' + dialCode;
        }

        const controlValue = this.getControlNativeValue();
        const hasValue = Boolean(
            this.valueControl?.value || controlValue?.trim()
        );

        if (!hasValue) {
            return undefined;
        }

        // Fallback to currently selected dropdown value if there is a value in the input but it doesn't contain a valid dial code, to avoid a dropdown state reset
        return this.dropdown?.value;
    }

    /**
     * Reapplies current raw value against an updated countries list.
     * @private
     */
    private updateControlsOnCountriesDataChange(): void {
        const valueWithDialCode = this.pickValueSourceWithDialCode();

        if (valueWithDialCode) {
            this.update(valueWithDialCode);
            this.control.pattern = this.resolveInputPattern(this.control);
            return;
        }

        // If there is no value with a dial code, trigger a regular update to apply the pattern and validation based on the new countries list
        this.handleDropdownChange();
    }

    /**
     * Rebuilds dropdown options from the current countries data.
     * @param {string} [selectedCode]
     * @private
     */
    private refreshDropdownOptions(selectedCode?: string): void {
        if (!this.dropdown?.nativeSelect) {
            return;
        }
        const options = this.buildCountriesOptions(selectedCode);
        this.dropdown.nativeSelect.replaceChildren(...options);
    }

    /**
     * Updates this instance after per-instance countries data changes.
     * @private
     */
    private handleCountriesDataChange(): void {
        if (!this.dropdown?.nativeSelect) {
            return;
        }

        this.refreshDropdownOptions(this.getDialCodeForOptionSelection());

        const hasSelectedOption = [...this.dropdown.nativeSelect.options].some(
            (option) => option.selected
        );

        if (!hasSelectedOption) {
            this.resetSelectionToFirstOption();
            return;
        }

        this.updateControlsOnCountriesDataChange();
    }

    /**
     * Resets dropdown selection to the first available option.
     * @private
     */
    private resetSelectionToFirstOption(): void {
        if (!this.dropdown?.nativeSelect?.options.length) {
            return;
        }

        this.dropdown.selectedIndex = 0;
        this.handleDropdownChange();
    }

    /**
     * Sets a built-in ruleset with a  preselected list of countries.
     * @param {("ee" | "lv" | "lt"| "default")} rules
     */
    static setRules(rules: UIPhoneFieldRulesKey): void {
        if (!UIPhoneField.isBalticCountry(rules)) {
            rules = 'default';
        }
        this.rules = rules;
    }

    /**
     * Checks if value is Estonia, Latvia, Lithuania country code iso2.
     * @param {string} iso2 - ISO2 country code.
     * @returns {boolean}
     */
    static isBalticCountry(iso2: string): boolean {
        return ['ee', 'lv', 'lt'].includes(iso2);
    }

    /**
     * Checks if this instance uses custom countries data.
     * @returns {boolean}
     */
    hasCustomCountriesData(): boolean {
        return (
            Array.isArray(this.countriesData) && this.countriesData.length > 0
        );
    }

    /**
     * Returns countries for this instance in their effective display order.
     * @returns {Array<UIPhoneFieldCountry>}
     * @private
     */
    private getResolvedCountries(): Array<UIPhoneFieldCountry> {
        if (this.hasCustomCountriesData()) {
            return this.countriesData!;
        }

        if (!Array.isArray(UIPhoneField.countries)) {
            return [];
        }

        return this.sortCountriesByRules(UIPhoneField.countries);
    }

    /**
     * Builds option elements with countries data.
     * @param {string} [selectedCode]
     * @returns {Array<HTMLOptionElement>}
     * @private
     */
    private buildCountriesOptions(
        selectedCode?: string
    ): Array<HTMLOptionElement> {
        return this.getResolvedCountries().map((c) => {
            return createElement<HTMLOptionElement>({
                tagName: 'option',
                attributes: {
                    'data-flag': c.iso2,
                    'value': '+' + c.dialCode,
                    'data-text': c.name,
                    'selected': this.clean(selectedCode) === c.dialCode || null,
                    'aria-label': c.name,
                },
            })!;
        });
    }

    /**
     * Sorts and filters countries by the current built-in ruleset.
     * @param {Array<UIPhoneFieldCountry>} countries
     * @returns {Array<UIPhoneFieldCountry>}
     * @private
     */
    private sortCountriesByRules(
        countries: Array<UIPhoneFieldCountry>
    ): Array<UIPhoneFieldCountry> {
        const { sortOrder } = UIPhoneField.RULES[this.resolveRules()];
        return sortOrder
            .map((iso2) => countries.find((country) => country.iso2 === iso2))
            .filter(Boolean) as Array<UIPhoneFieldCountry>;
    }

    /**
     * Gets the country by global class property or attribute. Attribute
     * has higher priority.
     * @returns {string}
     */
    private resolveRules(): UIPhoneFieldRulesKey {
        return (this.rules as UIPhoneFieldRulesKey) || UIPhoneField.rules;
    }

    /**
     * Resolve input pattern
     * @param {HTMLInputElement} control
     * @returns {string}
     */
    private resolveInputPattern(control: HTMLInputElement): string {
        if (control.dataset.customPattern) {
            return control.dataset.customPattern;
        }
        let countryCode: UIPhoneFieldRulesKey;
        try {
            countryCode = this.dropdown.nativeSelect.selectedOptions[0].dataset
                .flag! as UIPhoneFieldRulesKey;
        } catch (ex) {
            countryCode = this.resolveRules();
        }
        if (!UIPhoneField.isBalticCountry(countryCode)) {
            countryCode = 'default';
        }
        return UIPhoneField.RULES[countryCode].pattern;
    }

    /**
     * Returns value without '+' plus sign.
     * @param {string} [code]
     * @returns {string}
     */
    private clean(code?: string): string {
        // ^ Start of the line
        // \+* Any number or zero of plus (+) symbol
        return String(code || '').replace(/^\+*/, '');
    }

    /**
     * Gets the country data by value.
     * @param {string} [code]
     * @returns {UIPhoneFieldCountry}
     */
    getCountryByDialCode(code?: string): UndefinedOr<UIPhoneFieldCountry> {
        const dialCode = this.clean(code);
        return this.getResolvedCountries().find((c) => c.dialCode === dialCode);
    }

    /**
     * Sets the controls value depends on the prefilled value in control.
     * @param {string} val
     * @returns {UIPhoneFieldObjectValue}
     */
    parseValues(val: string): UIPhoneFieldParsedValue {
        val = this.clean(val);
        let dialCode = this.resolveDialCodeFromValue(val);
        const phone = val.substring(dialCode.length);
        if (!dialCode && phone) {
            dialCode = this.getDefaultDialCode();
        }
        return {
            dialCode: dialCode ? '+' + dialCode : '',
            phone,
        };
    }

    /**
     * Updates the dropdown and control values, based on forced or argument value.
     * @param {string} [forceValue]
     */
    update(forceValue?: string): void {
        const nativeValue = this.getControlNativeValue();
        const parts = this.parseValues(this.clean(forceValue ?? nativeValue));
        this.dropdown.value = parts.dialCode;
        setNativeInputValue(this.control, parts.phone);
        setNativeInputValue(this.valueControl, parts.dialCode + parts.phone);
    }

    /**
     * Gets default dial code for current country.
     * @returns {string}
     */
    getDefaultDialCode(): string {
        const defaultCountry = this.getResolvedCountries()[0];
        return defaultCountry ? defaultCountry.dialCode : '';
    }

    /**
     * This is a bit ugly, but need to provide backwards compatibility for
     * ibank where phone fields aren't yet in ui-fields. Actually this is only
     * for visual part and doesn't perform real validation.
     *
     * TODO This method should be removed when the ibank's forms will be refactored to ui-field.
     * @private
     */
    private performCustomValidation(): void {
        if (this.dropdown.nativeSelect.dataset.novalidate) {
            return;
        }
        const isValid = FormValidator.validateField(this.control);
        const field = this.closest<UIField>('ui-field');
        let errHint = this.parentElement!.querySelector<UIHint>(
            'ui-hint[error-type="customPhoneFieldError"]'
        )!;

        if (!isValid && !field) {
            this.classList.add('-error');

            const msg = FormValidator.translateValidationMessage(this.control);
            errHint =
                errHint ||
                createElement<UIHint>({
                    tagName: 'ui-hint',
                    attributes: {
                        'type': 'error',
                        'error-type': 'customPhoneFieldError',
                        'aria-live': 'polite',
                    },
                    children: msg ? [msg] : [],
                });
            this.control.setAttribute('aria-invalid', 'true');
            this.insertAdjacentElement('afterend', errHint);
        } else {
            this.control.removeAttribute('aria-invalid');
            this.classList.remove('-error');
            errHint?.remove();
        }
    }

    /**
     * Handles dropdown change event.
     * @private
     */
    private handleDropdownChange(): void {
        this.control.pattern = this.resolveInputPattern(this.control);
        this.handleValueChange();
        this.dispatchObjectOnChange();
    }

    /**
     * Sets the value inside hidden field, dropdown + input value.
     * @private
     */
    private handleValueChange(): void {
        this.performCustomValidation();
        const currentNativeValue = this.getControlNativeValue();
        // ^ Start of the line
        // \+ Plus symbol
        // \d{1,4} From 1 to 4 digits
        const foundDialCode = /^\+\d{1,4}/.exec(currentNativeValue);
        if (foundDialCode) {
            const opt = [].find.call(
                this.dropdown.nativeSelect.options,
                (opt: HTMLOptionElement) => {
                    return opt.value && opt.value === foundDialCode[0];
                }
            );
            if (opt) {
                this.dropdown.value = foundDialCode[0];
                setNativeInputValue(
                    this.control,
                    currentNativeValue.replace(foundDialCode[0], '')
                );
            }
        }
        const latestNativeValue = this.getControlNativeValue();
        if (latestNativeValue.trim()) {
            setNativeInputValue(
                this.valueControl,
                this.dropdown.value + latestNativeValue
            );
        } else {
            setNativeInputValue(this.valueControl, '');
        }
        this.dispatchObjectOnChange();
    }

    /**
     * Fires on any change which sends the object of two values of type UIPhoneFieldObjectValue.
     * @private
     */
    private dispatchObjectOnChange(): void {
        const parts = this.parseValues(this.clean(this.valueControl.value));
        dispatchCustomEvent(this, UIPhoneField.ObjectChangeEvent, {
            dialCode: parts.dialCode || this.dropdown.nativeSelect.value,
            phone: parts.phone,
        });
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement {
        return this.valueControl;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: AttributeValue): void {
        if (!newValue) {
            this.initialValue = '';
            setNativeInputValue(this.control, '');
            setNativeInputValue(this.valueControl, '');
        } else {
            const strValue = String(newValue);
            if (strValue.startsWith('+')) {
                this.update(strValue);
            } else {
                setNativeInputValue(this.control, strValue);
            }
        }
        this.dispatchObjectOnChange();
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.control.disabled = newState;
        this.dropdown.disabled = newState;
        this.valueControl.disabled = newState;
    }

    /**
     * @inheritDoc
     */
    onReadonlyStateChanged(newState: boolean): void {
        this.control.readOnly = newState;
        this.valueControl.readOnly = newState;
    }

    /** @private */
    private subscribeEvents(): void {
        this.dropdown?.addEventListener('change', this.dropdownChangeHandler);
        this.control.addEventListener('input', this.valueChangeHandler);
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.dropdown?.removeEventListener(
            'change',
            this.dropdownChangeHandler
        );
        this.control.removeEventListener('input', this.valueChangeHandler);
    }

    /**
     * Synchronizes aria-labels for the phone input and country code dropdown using the parent field label.
     *
     * Example: with ui-field label "Mobile phone", generated
     * aria-label values are "Mobile phone number" for the input and "Mobile phone country code" for the dropdown.
     * @private
     */
    private syncFieldAccessibleLabels(): void {
        const fieldLabel = this.closest<UIField>('ui-field')?.label;
        if (!fieldLabel) {
            return;
        }

        const countryCodeAriaLabel = `${fieldLabel} ${UIDropdown.labels.countryCode}`;
        this.control.setAttribute(
            'aria-label',
            `${fieldLabel} ${UIPhoneField.labels.NUMBER}`
        );
        this.dropdown.setAttribute('aria-label', countryCodeAriaLabel);
        this.dropdown.nativeSelect.setAttribute(
            'aria-label',
            countryCodeAriaLabel
        );
        this.dropdown.syncFieldAriaReferences();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const control = this.queryChildren('input')[0] as HTMLInputElement;
        if (!control) {
            throw new Error(
                this.constructor.name + ' : non-input element given'
            );
        }

        this.initialValue = control.value;
        const parts = this.parseValues(this.clean(control.value));
        const countriesOptions = this.buildCountriesOptions(parts.dialCode);
        const cName = control.name;
        const cId = control.id;
        const fieldLabel = this.closest<UIField>('ui-field')?.label;
        const phoneNumberAriaLabel = fieldLabel
            ? `${fieldLabel} ${UIPhoneField.labels.NUMBER}`
            : UIPhoneField.labels.PHONENR;
        const countryCodeAriaLabel = fieldLabel
            ? `${fieldLabel} ${UIDropdown.labels.countryCode}`
            : null;

        if (control.pattern) {
            control.dataset.customPattern = control.pattern;
        }

        rebuild(control, {
            tagName: 'input',
            attributes: {
                'type': 'tel',
                'required': control.required || null,
                'disabled': control.disabled || null,
                'readonly': control.readOnly || null,
                'pattern': this.resolveInputPattern(control),
                'maxlength': 12,
                'data-pattern-error': UIPhoneField.labels.INVALID,
                'data-novalidate': control.dataset.novalidate || null,
                'value':
                    control.readOnly && control.value
                        ? `${parts.dialCode} ${parts.phone}`
                        : parts.phone || null,
                'aria-label': phoneNumberAriaLabel,
                'data-custom-pattern': control.dataset.customPattern || null,
                // should remove name and id, then set it to actual this.valueControl
                // which will go to the server or other handler.
                // 'name': null,
                // 'id': null
            },
            classList: {
                'ui-phonefield__control': true,
            },
        });

        this.insertElements([
            control.readOnly
                ? undefined
                : {
                      tagName: 'ui-dropdown',
                      classList: {
                          'ui-phonefield__dropdown': true,
                      },
                      attributes: {
                          'layout': 'dial',
                          'popover-width': UIDropdown.PopoverWidth.Small,
                          'maxlength': UIPhoneField.MAX_COUNTRIES_DISPLAY,
                          // Set control-id so dropdown can set up proper aria-labelledby
                          // This will reference both the field label and the selected value
                          'control-id': this.controlId
                              ? `${this.controlId}-dropdown`
                              : null,
                          'aria-label': countryCodeAriaLabel,
                      },
                      children: [
                          {
                              tagName: 'select',
                              attributes: {
                                  'aria-hidden': 'true',
                                  'aria-label': countryCodeAriaLabel,
                                  'disabled': control.disabled || null,
                              },
                              children: countriesOptions,
                          },
                      ],
                  },
            {
                element: control,
            },
            {
                tagName: 'input',
                classList: {
                    'ui-phonefield__value': true,
                },
                attributes: {
                    type: 'hidden',
                    name: cName || null,
                    id: cId || null,
                    value: parts.dialCode + parts.phone,
                },
            },
        ]);

        if (this.dropdown?.control && !fieldLabel) {
            this.dropdown.control.setAttribute(
                'aria-label',
                UIPhoneField.labels.CHOOSE_COUNTRY as string
            );
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();

        // Force ignore validation for native select.
        runInNextFrame(() => {
            if (this.dropdown?.nativeSelect) {
                this.dropdown.nativeSelect.dataset.novalidate = 'true';
            }
            this.syncFieldAccessibleLabels();
        });
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}

UIPhoneField.setRules('default');
UIPhoneField.countries = countries[UIElement.LANGUAGE];
